/**
 * 持卡人门户 API 边界（live）
 * 正式前缀：/cardholder/api/v1
 *
 * FE-E2b 薄壳化：公共核心下沉 @shared/api/createClient 工厂；本文件保留
 * customer 端配置、类型与端特有分支（语言化登出 / X-Cardholder-Id 回退 / PUT 幂等豁免），
 * 导出面与行为不变（FE-E2a 契约测试 client.contract.test.ts 锁定）。
 */
import { createClient } from '@shared/api/createClient';

export const DEFAULT_CARDHOLDER_ID = 30;
export const DEFAULT_MERCHANT_ID = 20;

/**
 * 统一结果：失败分支携带业务错误码（信封 code，如 CONC_409 / VAL_400）；
 * 网络错/超时（未获响应）code 为 undefined，调用方可据此区分业务失败与传输失败。
 * traceId 为后端信封追踪码（系统级错误时 error 已附加，字段供页面自行展示）。
 */
export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; code?: string; traceId?: string };

/** live-only（阻断级约定延续：生产构建禁止产出纯 mock dist）：mock 回调保留但恒关 */
export const USE_MOCK = false;

/** 会话写入后广播，供 RequireSetup 重读 me 标志 */
export const SESSION_CHANGED_EVENT = 'allinks-session';

// ================= 会话与领域视图类型（对齐后端 DTO） =================

/** 认证渠道（对齐后端 AuthChannel 枚举字面量） */
export type AuthChannel = 'ADMIN' | 'AGENT' | 'MERCHANT' | 'CARDHOLDER';

/** 当前会话身份视图（对齐后端 PortalSessionView record） */
export type PortalSessionView = {
  subjectId?: number;
  channel?: AuthChannel;
  principal?: string;
  roles?: string[];
  permissions?: string[];
  merchantId?: number;
  agentId?: number;
  cardholderId?: number;
  emailVerified?: boolean;
  emailMasked?: string;
  mustChangePassword?: boolean;
  mfaSetupRequired?: boolean;
  /** OTP 宽限剩余秒；无宽限为 0（对齐后端 PortalSessionView） */
  otpGraceRemainingSeconds?: number;
  /** 是否已设置支付密码（S10a；CARDHOLDER 渠道有意义） */
  payPasswordSet?: boolean;
  /** 是否须引导设置支付密码（S10a；CARDHOLDER = 未设置；RequireSetup 门禁依据） */
  payPasswordSetupRequired?: boolean;
  /** 敏感操作验证方式：CARDHOLDER='PAY_PASSWORD_OR_2FA'（二选一）；其他门户='2FA' */
  sensitiveVerifyMode?: 'PAY_PASSWORD_OR_2FA' | '2FA' | string;
  /** 是否为运营代登录会话（契约 §0.4；后端 LoginSetupGate 对代登录会话豁免 3423/3412 引导） */
  impersonated?: boolean;
};

/** 运行时菜单节点（对齐后端 MenuNodeView record） */
export type MenuNodeView = {
  id: string;
  title: string;
  path?: string | null;
  permission?: string | null;
  children?: MenuNodeView[];
};

export type CardholderSession = {
  accessToken?: string;
  refreshToken?: string;
  /** dev 回退用：无 accessToken 时走 X-Cardholder-Id（保留必填，向后兼容） */
  cardholderId: string;
  principal?: string;
  /** /auth/me 结果（登录后写入；拉取失败置 null） */
  me?: PortalSessionView | null;
  /** /auth/menus 结果（登录后写入；拉取失败置 null） */
  menus?: MenuNodeView[] | null;
  /** accessToken 预估过期时间戳（ms）；缺省表示未知 */
  expiresAt?: number;
  /** 是否为运营代登录会话（/portal-login 落地页写入；代登录无 refreshToken 不可续期） */
  impersonated?: boolean;
};

// ================= 工厂实例（端差异参数化） =================

const core = createClient<CardholderSession>({
  apiBase: {
    dev: '/cardholder/api/v1',
    prod: 'https://allinks-api.swapsle.com/cardholder/api/v1',
  },
  sessionKey: 'allinks_cardholder_session',
  // 兜底：无 App 注入 handler 时跳 /{lang}/login（从当前路径或 storage 取语言；默认 en）
  loginPath: () => {
    const m = window.location.pathname.match(/^\/(zh|en)(\/|$)/);
    const lang = m?.[1] === 'zh' ? 'zh' : (localStorage.getItem('allinks_chd_lang') === 'zh' ? 'zh' : 'en');
    return `/${lang}/login`;
  },
  // customer 特有：无 accessToken 时回退 X-Cardholder-Id（会话 cardholderId 优先，缺省 30）
  extraHeaders: (s): Record<string, string> =>
    s?.accessToken ? {} : { 'X-Cardholder-Id': s?.cardholderId || String(DEFAULT_CARDHOLDER_ID) },
  // 自定义 headers 直接 spread 合并（调用方可覆盖同名字段，不做 Authorization 过滤）
  customHeaderMode: 'spread',
  // customer 信封 error 公式：`CODE: message` 前缀拼接（双有拼、缺单边取非空侧）
  errorFormat: 'code-prefix',
  // PUT（US-PREF-2 偏好更新）幂等豁免契约（合同 §3.7 A2）——严禁携带 Idempotency-Key
  putIdempotencyExempt: true,
  // JSON 写路径幂等键回退形态带随机后缀；上传路径保持原内联 idem-{ts}（无后缀）
  jsonKeyGen: 'ts-random36',
  uploadKeyGen: 'ts',
});

export const CARDHOLDER_API_BASE = core.apiBase;

export function cardholderApiUrl(path: string): string {
  return core.apiUrl(path);
}

export function loadSession(): CardholderSession | null {
  return core.loadSession();
}

export function saveSession(s: CardholderSession) {
  core.saveSession(s);
}

export function clearSession() {
  core.clearSession();
}

export function setAuthFailureHandler(fn: (() => void) | null) {
  core.setAuthFailureHandler(fn);
}

/** 清会话 + 跳登录：refresh 失败 / 非过期 401 / 主动登出均走此路径 */
export function signOutLocal() {
  core.signOutLocal();
}

// ================= 公共 API（保持既有签名，向后兼容） =================

export async function cardholderGet<T>(
  path: string,
  mock: () => T,
  opts?: { skipAuth?: boolean },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('GET', path, undefined, opts);
}

export async function cardholderPost<T>(
  path: string,
  body: unknown,
  mock: () => T,
  opts?: { skipAuth?: boolean; idempotencyKey?: string; headers?: Record<string, string> },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('POST', path, body, opts);
}

/**
 * PUT 请求（US-PREF-2）：复用 fetchWithRefresh 的 401 refresh 重放契约，
 * 但**不携带 Idempotency-Key**（偏好更新幂等豁免，合同 §3.1/§3.7 A2）——
 * opts 类型上即不提供 idempotencyKey 入口，防止 POST 的 key 注入逻辑被误复用。
 */
export async function cardholderPut<T>(
  path: string,
  body: unknown,
  mock: () => T,
  opts?: { skipAuth?: boolean; headers?: Record<string, string> },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('PUT', path, body, opts);
}

/** multipart 文件上传；与 JSON client 复用会话和 401 refresh 契约。 */
export async function cardholderUpload<T>(
  path: string,
  formData: FormData,
  mock: () => T,
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  const res = await core.upload<T>(path, formData);
  // 失败态保留业务 code（与其他入口一致）：调用方 mapApiError 依赖信封 code 分流，
  // 此前上传路径丢弃 code 会把业务失败误判为网络异常
  return res.ok
    ? { ok: true, data: res.data }
    : { ok: false, error: res.error, code: res.code, traceId: res.traceId };
}
